import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMyWebinarStats, webinarLink, type MyWebinarStats } from "@/services/webinar";
import QRCode from "qrcode";
import AffiliateShell from "@/components/feature/AffiliateShell";
import { fetchApprovedMessages, type ApprovedMessage } from "@/services/admin";
import {
  deltaPercent,
  fetchAchievementCatalog,
  fetchActivePrizes,
  fetchAffiliateDashboard,
  fetchLevels,
  logShare,
  referralLink,
  type AchievementItem,
  type AffiliateDashboard,
  type AffiliateLevel,
  type Kpi,
  type Prize,
  type ShareChannel,
} from "@/services/affiliate";

const PURPLE = "#7C3AED";

const fmt = (n: number) => n.toLocaleString("es");

function weekdayLabel(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const s = new Date(y, m - 1, d).toLocaleDateString("es", { weekday: "short" }).replace(".", "");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ---------------------------------------------------------------------------
// Piezas visuales
// ---------------------------------------------------------------------------

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <section className={`bg-white border border-slate-200 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`}>{children}</section>;
}

function Delta({ kpi }: { kpi: Kpi }) {
  const d = deltaPercent(kpi);
  if (d === null) return <span className="text-[11px] text-slate-400">Sin datos de la semana pasada</span>;
  const up = d >= 0;
  return (
    <span className={`text-[11px] font-semibold ${up ? "text-emerald-600" : "text-rose-500"}`}>
      <i className={up ? "ri-arrow-up-line" : "ri-arrow-down-line"}></i> {up ? "+" : ""}
      {d}% vs. semana pasada
    </span>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="fixed inset-0 bg-[#0B1F3A]/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-heading font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} aria-label="Cerrar" className="w-8 h-8 rounded-full hover:bg-slate-100 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

function WeekChart({ days }: { days: { day: string; visits: number }[] }) {
  const W = 600;
  const H = 190;
  const padL = 34;
  const padB = 24;
  const padT = 10;
  const max = Math.max(5, ...days.map((d) => d.visits));
  const top = Math.ceil(max / 5) * 5;
  const x = (i: number) => padL + (i * (W - padL - 12)) / Math.max(1, days.length - 1);
  const y = (v: number) => padT + (1 - v / top) * (H - padT - padB);
  const pts = days.map((d, i) => `${x(i)},${y(d.visits)}`);
  const line = pts.join(" ");
  const area = `${x(0)},${y(0)} ${line} ${x(days.length - 1)},${y(0)}`;
  const ticks = [0, top / 2, top];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Visitas a tu link por día de la semana">
      <defs>
        <linearGradient id="vv-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={PURPLE} stopOpacity="0.28" />
          <stop offset="1" stopColor={PURPLE} stopOpacity="0" />
        </linearGradient>
      </defs>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={padL} x2={W - 12} y1={y(t)} y2={y(t)} stroke="#E5E7EB" strokeWidth="1" />
          <text x={padL - 6} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#94A3B8">
            {Math.round(t)}
          </text>
        </g>
      ))}
      <polygon points={area} fill="url(#vv-area)" />
      <polyline points={line} fill="none" stroke={PURPLE} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {days.map((d, i) => (
        <g key={d.day}>
          <circle cx={x(i)} cy={y(d.visits)} r="4" fill="#fff" stroke={PURPLE} strokeWidth="2.5">
            <title>{`${weekdayLabel(d.day)}: ${d.visits} visitas`}</title>
          </circle>
          <text x={x(i)} y={H - 6} textAnchor="middle" fontSize="11" fill="#64748B">
            {weekdayLabel(d.day)}
          </text>
        </g>
      ))}
    </svg>
  );
}

function Donut({ segments, center, caption }: { segments: { value: number; color: string }[]; center: string; caption: string }) {
  const R = 52;
  const C = 2 * Math.PI * R;
  const total = segments.reduce((s, x) => s + x.value, 0);
  let offset = 0;
  return (
    <div className="relative w-36 h-36 flex-shrink-0">
      <svg viewBox="0 0 130 130" className="w-full h-full -rotate-90" role="img" aria-label={`Conversión de visitas ${center}`}>
        <circle cx="65" cy="65" r={R} fill="none" stroke="#E5E7EB" strokeWidth="16" />
        {total > 0
          ? segments.map((s, i) => {
              const len = (s.value / total) * C;
              const el = (
                <circle
                  key={i}
                  cx="65"
                  cy="65"
                  r={R}
                  fill="none"
                  stroke={s.color}
                  strokeWidth="16"
                  strokeDasharray={`${len} ${C - len}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += len;
              return el;
            })
          : null}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="font-heading font-extrabold text-2xl text-slate-900">{center}</span>
        <span className="text-[10px] leading-tight text-slate-500 px-6">{caption}</span>
      </div>
    </div>
  );
}

function Ring({ done, goal }: { done: number; goal: number }) {
  const R = 30;
  const C = 2 * Math.PI * R;
  const pct = Math.min(1, done / goal);
  return (
    <div className="relative w-20 h-20 flex-shrink-0">
      <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
        <circle cx="40" cy="40" r={R} fill="none" stroke="#E5E7EB" strokeWidth="7" />
        <circle cx="40" cy="40" r={R} fill="none" stroke="#20C997" strokeWidth="7" strokeLinecap="round" strokeDasharray={`${pct * C} ${C}`} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-heading font-extrabold text-slate-900">
        {done}/{goal}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

type ModalKind = null | "levels" | "prizes" | "achievements" | "ideas";

export default function Afiliado() {
  const navigate = useNavigate();
  const [data, setData] = useState<AffiliateDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [qr, setQr] = useState<string>("");
  const [toast, setToast] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalKind>(null);
  const [levels, setLevels] = useState<AffiliateLevel[]>([]);
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [catalog, setCatalog] = useState<AchievementItem[]>([]);
  const [ideas, setIdeas] = useState<ApprovedMessage[]>([]);
  const [wbStats, setWbStats] = useState<MyWebinarStats | null>(null);

  const link = data ? referralLink(data.code) : "";

  useEffect(() => {
    fetchMyWebinarStats(30).then(setWbStats).catch(() => setWbStats(null));
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  };

  const load = useCallback(async (offset: number) => {
    try {
      setData(await fetchAffiliateDashboard(offset));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cargar tu panel.");
    }
  }, []);

  useEffect(() => {
    let active = true;
    load(weekOffset).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [load, weekOffset]);

  useEffect(() => {
    if (!link) return;
    let active = true;
    QRCode.toDataURL(link, { width: 320, margin: 1, color: { dark: "#0B1F3A", light: "#FFFFFF" } })
      .then((url) => {
        if (active) setQr(url);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [link]);

  const track = async (channel: ShareChannel) => {
    await logShare(channel).catch(() => undefined);
    load(weekOffset);
  };

  const inviteText = `¡Hola! 👋 Estoy descubriendo Vívelo AI, una plataforma para crecer en hábitos, formación y comunidad. Te invito a hacer el test de perfil emprendedor aquí: ${link}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      showToast("Link copiado");
    } catch {
      showToast("No se pudo copiar. Selecciona el link y cópialo.");
    }
    track("copy");
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(inviteText)}`, "_blank", "noopener,noreferrer");
    track("whatsapp");
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Vívelo AI", text: "Te invito a Vívelo AI", url: link });
        track("share");
      } catch {
        /* la persona cerró el menú de compartir */
      }
    } else {
      await copyLink();
    }
  };

  const openModal = async (kind: Exclude<ModalKind, null>) => {
    setModal(kind);
    try {
      if (kind === "levels") setLevels(await fetchLevels());
      if (kind === "prizes") setPrizes(await fetchActivePrizes());
      if (kind === "achievements") setCatalog(await fetchAchievementCatalog());
      if (kind === "ideas") setIdeas((await fetchApprovedMessages()).filter((m) => m.active).slice(0, 6));
    } catch {
      showToast("No se pudo cargar la información.");
    }
  };

  const funnel = useMemo(() => {
    if (!data) return null;
    const k = data.kpis;
    const conversion = k.visits.total > 0 ? Math.round((k.prospects.total / k.visits.total) * 100) : 0;
    return {
      conversion,
      rows: [
        { label: "Visitas", value: k.visits.total, color: "#20C997" },
        { label: "Test completados", value: k.tests.total, color: PURPLE },
        { label: "Prospectos", value: k.prospects.total, color: "#F97316" },
        { label: "Leads calientes", value: k.hot.total, color: "#FACC15" },
        { label: "Clientes", value: k.clients.total, color: "#94A3B8" },
      ],
    };
  }, [data]);

  if (loading) {
    return (
      <AffiliateShell>
        <div className="py-24 text-center text-slate-500">
          <i className="ri-loader-4-line animate-spin text-3xl"></i>
          <p className="mt-2 text-sm">Cargando tu panel de afiliado…</p>
        </div>
      </AffiliateShell>
    );
  }

  if (error || !data) {
    return (
      <AffiliateShell>
        <div className="max-w-xl mx-auto mt-16 bg-white border border-dashed border-rose-300 rounded-2xl p-10 text-center text-rose-600 text-sm">
          {error ?? "Todavía no tienes un perfil de afiliado en esta organización."}
        </div>
      </AffiliateShell>
    );
  }

  const k = data.kpis;
  const kpiCards = [
    { label: "Visitas a tu link", icon: "ri-eye-line", tint: "bg-sky-50 text-sky-600", kpi: k.visits },
    { label: "Test completados", icon: "ri-clipboard-line", tint: "bg-emerald-50 text-emerald-600", kpi: k.tests },
    { label: "Prospectos", icon: "ri-team-line", tint: "bg-violet-50 text-violet-600", kpi: k.prospects },
    { label: "Leads calientes", icon: "ri-fire-line", tint: "bg-orange-50 text-orange-500", kpi: k.hot },
  ];

  return (
    <AffiliateShell>
      <div className="max-w-6xl mx-auto flex flex-col gap-4">
        {/* Encabezado + recompensas */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
          <div className="lg:col-span-8 flex flex-col justify-center">
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-[#0B1F3A] flex items-center gap-2">
              <i className="ri-vip-crown-2-fill" style={{ color: PURPLE }}></i> Panel de Afiliado
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-md">
              Comparte Vívelo AI y ayuda a más personas a transformar su vida mientras construyes tu negocio.
            </p>
          </div>
          <Card className="lg:col-span-4 p-4 flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center flex-shrink-0">
              <i className="ri-gift-2-fill text-2xl"></i>
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-slate-900">Tus recompensas</div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Nivel {data.level.title} · {fmt(data.points)} pts</span>
                <span className="font-semibold text-slate-700">{data.level.percent}%</span>
              </div>
              <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={data.level.percent} aria-valuemin={0} aria-valuemax={100}>
                <div className="h-full rounded-full" style={{ width: `${data.level.percent}%`, background: PURPLE }}></div>
              </div>
              <button onClick={() => openModal("levels")} className="mt-1.5 text-xs font-semibold cursor-pointer" style={{ color: PURPLE }}>
                Ver beneficios y niveles <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </Card>
        </div>

        {/* Link + QR + próxima acción */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Card className="lg:col-span-8 p-5" >
            <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
              <div className="md:col-span-3">
                <h2 className="font-heading font-bold text-slate-900">Tu link único</h2>
                <p className="text-xs text-slate-500 mt-0.5">Comparte tu enlace personal y comienza a impactar vidas.</p>
                <div className="mt-3 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2.5">
                  <input readOnly value={link} aria-label="Tu link único" onFocus={(e) => e.currentTarget.select()} className="flex-1 min-w-0 bg-transparent text-xs text-slate-700 outline-none" />
                  <button onClick={copyLink} aria-label="Copiar link" className="w-8 h-8 rounded-lg hover:bg-slate-100 cursor-pointer" style={{ color: PURPLE }}>
                    <i className="ri-file-copy-line text-lg"></i>
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={shareWhatsApp} className="px-4 py-2.5 rounded-lg bg-[#20A464] hover:bg-[#1b8f57] text-white text-xs font-semibold cursor-pointer whitespace-nowrap">
                    <i className="ri-whatsapp-line mr-1.5"></i>Compartir por WhatsApp
                  </button>
                  <button onClick={shareLink} className="px-4 py-2.5 rounded-lg border text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-violet-50" style={{ color: PURPLE, borderColor: PURPLE }}>
                    <i className="ri-share-line mr-1.5"></i>Compartir enlace
                  </button>
                </div>
              </div>
              <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-3">
                <div>
                  <h3 className="font-heading font-bold text-slate-900 text-sm">Código QR</h3>
                  <p className="text-[11px] text-slate-500">Escanéalo para registrarse</p>
                </div>
                <div className="flex flex-col items-center gap-1.5">
                  {qr ? (
                    <img src={qr} alt="Código QR de tu link" className="w-32 h-32 rounded-lg border border-slate-200 p-1 bg-white" />
                  ) : (
                    <div className="w-32 h-32 rounded-lg bg-slate-100 animate-pulse"></div>
                  )}
                  {qr ? (
                    <a href={qr} download="vivelo-qr.png" onClick={() => track("qr")} className="text-[11px] font-semibold cursor-pointer" style={{ color: PURPLE }}>
                      <i className="ri-download-2-line"></i> Descargar QR
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </Card>

          <Card className="lg:col-span-4 p-5 flex flex-col">
            <h2 className="font-heading font-bold text-slate-900 flex items-center gap-2">
              <i className="ri-rocket-2-fill text-emerald-500"></i> Tu próxima acción
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Logra que {data.invitesGoal.goal} personas se registren con tu link esta semana y gana 100 puntos extra.
            </p>
            <div className="mt-3 flex items-center gap-4">
              <Ring done={Math.min(data.invitesGoal.done, data.invitesGoal.goal)} goal={data.invitesGoal.goal} />
              <div className="text-xs text-slate-600">
                Personas
                <br />
                invitadas
              </div>
            </div>
            <button onClick={() => openModal("ideas")} className="mt-auto pt-0 w-full">
              <span className="mt-4 block w-full py-2.5 rounded-lg bg-[#20C997] hover:bg-[#1bb386] text-[#0B1F3A] text-xs font-bold cursor-pointer">
                Ver ideas para invitar <i className="ri-lightbulb-flash-line"></i>
              </span>
            </button>
          </Card>
        </div>

        {/* KPIs */}
        <Card className="p-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {kpiCards.map((c) => (
              <div key={c.label} className="rounded-xl border border-slate-100 p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${c.tint}`}>
                    <i className={c.icon}></i>
                  </span>
                  {c.label}
                </div>
                <div className="mt-2 font-heading font-extrabold text-3xl text-slate-900">{fmt(c.kpi.total)}</div>
                <Delta kpi={c.kpi} />
              </div>
            ))}
          </div>
        </Card>

        {/* Progreso semanal + premios */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Card className="lg:col-span-7 p-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-heading font-bold text-slate-900">Tu progreso esta semana</h2>
              <select
                value={weekOffset}
                onChange={(e) => setWeekOffset(Number(e.target.value))}
                aria-label="Semana"
                className="text-xs font-semibold border border-violet-200 text-violet-700 bg-violet-50 rounded-lg px-2.5 py-1.5 cursor-pointer outline-none"
              >
                <option value={0}>Esta semana</option>
                <option value={-1}>Semana pasada</option>
              </select>
            </div>
            <div className="mt-3">
              <WeekChart days={data.week.days} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3">
              {[
                { label: "Invitaciones enviadas", value: data.week.invitesSent, icon: "ri-send-plane-fill", tint: "text-sky-500" },
                { label: "Nuevos registros", value: data.week.newSignups, icon: "ri-user-add-fill", tint: "text-emerald-500" },
                { label: "Nuevos clientes", value: data.week.newClients, icon: "ri-star-fill", tint: "text-amber-500" },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-2">
                  <i className={`${s.icon} text-lg ${s.tint}`}></i>
                  <div>
                    <div className="text-[10px] text-slate-500 leading-tight">{s.label}</div>
                    <div className="font-heading font-extrabold text-lg text-slate-900 leading-tight">{s.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="lg:col-span-5 p-5 flex flex-col">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-slate-900">Top de premios</h2>
              <button onClick={() => openModal("prizes")} className="text-xs font-semibold cursor-pointer" style={{ color: PURPLE }}>
                Ver todos
              </button>
            </div>
            <ul className="mt-3 divide-y divide-slate-100">
              {data.prizes.map((p) => (
                <li key={p.id} className="py-3 flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <span className="flex-1 text-sm text-slate-800">{p.title}</span>
                  <span className="text-xs font-bold text-slate-700 whitespace-nowrap">{fmt(p.pointsCost)} pts</span>
                </li>
              ))}
              {data.prizes.length === 0 ? <li className="py-6 text-sm text-slate-500 text-center">Tu administrador publicará los premios pronto.</li> : null}
            </ul>
            <button onClick={() => openModal("prizes")} className="mt-auto pt-3 text-left text-xs font-semibold cursor-pointer" style={{ color: PURPLE }}>
              Ver todos los premios disponibles <i className="ri-arrow-right-line"></i>
            </button>
          </Card>
        </div>

        {/* Rendimiento + logros */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Card className="lg:col-span-7 p-5">
            <h2 className="font-heading font-bold text-slate-900">Rendimiento general</h2>
            <p className="text-xs text-slate-500">Tu impacto en la comunidad Vívelo AI</p>
            <div className="mt-4 flex items-center gap-6 flex-wrap">
              {funnel ? (
                <Donut segments={funnel.rows.map((r) => ({ value: r.value, color: r.color }))} center={`${funnel.conversion}%`} caption="Conversión de visitas" />
              ) : null}
              <ul className="flex-1 min-w-[180px] flex flex-col gap-2">
                {funnel?.rows.map((r) => (
                  <li key={r.label} className="flex items-center gap-2 text-sm">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: r.color }}></span>
                    <span className="flex-1 text-slate-600">{r.label}</span>
                    <span className="font-bold text-slate-900">{fmt(r.value)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>

          <Card className="lg:col-span-5 p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-slate-900">Logros recientes</h2>
              <button onClick={() => openModal("achievements")} className="text-xs font-semibold cursor-pointer" style={{ color: PURPLE }}>
                Ver todos
              </button>
            </div>
            <ul className="mt-3 divide-y divide-slate-100">
              {data.achievements.map((a) => (
                <li key={a.title} className="py-3 flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0">
                    <i className={`${a.icon} text-lg`}></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900 truncate">{a.title}</div>
                    <div className="text-[11px] text-slate-500 truncate">{a.description}</div>
                  </div>
                  <span className="text-xs font-bold text-emerald-600 whitespace-nowrap">+{a.points} pts</span>
                </li>
              ))}
              {data.achievements.length === 0 ? (
                <li className="py-6 text-sm text-slate-500 text-center">Comparte tu link para desbloquear tu primer logro.</li>
              ) : null}
            </ul>
          </Card>
        </div>

        {/* Webinar */}
        <Card className="p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <span className="w-12 h-12 rounded-xl flex items-center justify-center bg-violet-50 text-violet-600 flex-shrink-0">
              <i className="ri-live-line text-2xl"></i>
            </span>
            <div className="flex-1 min-w-0">
              <h2 className="font-heading font-bold text-slate-900">Webinar de Vívelo</h2>
              <p className="text-xs text-slate-600 mt-0.5">
                Tu link único registra quién entró por ti y desde qué canal.
                {wbStats ? ` Últimos 30 días: ${wbStats.leads} invitados, ${wbStats.completed} lo terminaron.` : ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(webinarLink(data.code, "link"));
                    showToast("Link del webinar copiado");
                  } catch {
                    showToast("No se pudo copiar.");
                  }
                }}
                className="px-4 py-2.5 rounded-lg text-white text-sm font-bold cursor-pointer whitespace-nowrap"
                style={{ background: PURPLE }}
              >
                <i className="ri-file-copy-line mr-1.5"></i>Copiar link
              </button>
              <button onClick={() => navigate("/app/webinar")} className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold cursor-pointer whitespace-nowrap">
                Ver mis invitados
              </button>
            </div>
          </div>
        </Card>

        {/* CTA */}
        <section className="rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4 border border-violet-100 bg-violet-50/60">
          <span className="w-14 h-14 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{ background: PURPLE }}>
            <i className="ri-group-fill text-2xl"></i>
          </span>
          <div className="flex-1">
            <h2 className="font-heading font-bold text-slate-900">Ayuda a más personas a transformar su vida</h2>
            <p className="text-xs text-slate-600 mt-0.5">Cada persona que invitas puede descubrir su propósito y desarrollar su potencial.</p>
          </div>
          <button onClick={shareLink} className="px-5 py-3 rounded-lg text-white text-sm font-bold cursor-pointer whitespace-nowrap" style={{ background: PURPLE }}>
            <i className="ri-share-line mr-1.5"></i>Compartir mi link ahora
          </button>
        </section>
      </div>

      {/* Modales */}
      {modal === "levels" ? (
        <Modal title="Niveles y beneficios" onClose={() => setModal(null)}>
          <p className="text-sm text-slate-600">
            Ganas puntos cuando alguien se registra con tu link (+50), completa el test (+30), se convierte en cliente (+500) y al desbloquear logros.
          </p>
          <ul className="mt-4 flex flex-col gap-2">
            {levels.map((l) => {
              const reached = data.points >= l.minPoints;
              return (
                <li key={l.id} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${l.title === data.level.title ? "border-violet-300 bg-violet-50" : "border-slate-200"}`}>
                  <i className={`${reached ? "ri-checkbox-circle-fill text-emerald-500" : "ri-lock-line text-slate-400"} text-xl`}></i>
                  <span className="flex-1 font-semibold text-slate-900 text-sm">{l.title}</span>
                  <span className="text-xs text-slate-600">{fmt(l.minPoints)} pts</span>
                </li>
              );
            })}
          </ul>
          <p className="mt-3 text-xs text-slate-500">
            Llevas {fmt(data.points)} pts.{" "}
            {data.level.nextTitle && data.level.nextMin !== null
              ? `Te faltan ${fmt(data.level.nextMin - data.points)} pts para ${data.level.nextTitle}.`
              : "¡Ya estás en el nivel más alto!"}
          </p>
        </Modal>
      ) : null}

      {modal === "prizes" ? (
        <Modal title="Premios disponibles" onClose={() => setModal(null)}>
          <ul className="flex flex-col gap-2">
            {prizes.map((p) => (
              <li key={p.id} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
                <span className="w-9 h-9 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center">
                  <i className={`${p.icon} text-xl`}></i>
                </span>
                <span className="flex-1 text-sm font-semibold text-slate-900">{p.title}</span>
                <span className="text-xs font-bold text-slate-700 whitespace-nowrap">{fmt(p.pointsCost)} pts</span>
              </li>
            ))}
            {prizes.length === 0 ? <li className="text-sm text-slate-500 text-center py-4">Todavía no hay premios publicados.</li> : null}
          </ul>
        </Modal>
      ) : null}

      {modal === "achievements" ? (
        <Modal title="Todos los logros" onClose={() => setModal(null)}>
          <ul className="flex flex-col gap-2">
            {catalog.map((a) => (
              <li key={a.id} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${a.unlocked ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 opacity-70"}`}>
                <span className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0">
                  <i className={`${a.unlocked ? a.icon : "ri-lock-line"} text-lg`}></i>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-slate-900">{a.title}</div>
                  <div className="text-[11px] text-slate-500">{a.description}</div>
                </div>
                <span className="text-xs font-bold text-emerald-600 whitespace-nowrap">+{a.points} pts</span>
              </li>
            ))}
          </ul>
        </Modal>
      ) : null}

      {modal === "ideas" ? (
        <Modal title="Ideas para invitar" onClose={() => setModal(null)}>
          <p className="text-sm text-slate-600">Mensajes aprobados por tu equipo. Elige uno y compártelo con tu link.</p>
          <ul className="mt-3 flex flex-col gap-3">
            {[{ id: "default", title: "Invitación sencilla", content: inviteText.replace(` ${link}`, "") }, ...ideas.map((m) => ({ id: m.id, title: m.title, content: m.content }))].map((m) => (
              <li key={m.id} className="rounded-xl border border-slate-200 p-3">
                <div className="text-xs font-bold text-slate-900">{m.title}</div>
                <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{m.content}</p>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => {
                      const full = `${m.content} ${link}`;
                      // Copia el mensaje automáticamente (por si WhatsApp no lo precarga) y abre WhatsApp.
                      navigator.clipboard
                        .writeText(full)
                        .then(() => showToast("Mensaje copiado. Pégalo en WhatsApp si no aparece."))
                        .catch(() => undefined);
                      window.open(`https://wa.me/?text=${encodeURIComponent(full)}`, "_blank", "noopener,noreferrer");
                      track("whatsapp");
                    }}
                    className="px-3 py-1.5 rounded-lg bg-[#20A464] text-white text-xs font-semibold cursor-pointer"
                  >
                    <i className="ri-whatsapp-line mr-1"></i>WhatsApp
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(`${m.content} ${link}`);
                        showToast("Mensaje copiado");
                      } catch {
                        showToast("No se pudo copiar.");
                      }
                      track("copy");
                    }}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold cursor-pointer"
                  >
                    <i className="ri-file-copy-line mr-1"></i>Copiar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Modal>
      ) : null}

      {toast ? (
        <div className="fixed bottom-24 lg:bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-[#0B1F3A] text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">{toast}</div>
      ) : null}
    </AffiliateShell>
  );
}
